import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, X } from 'lucide-react';

interface LessonRowProps {
  lesson: string;
  onChange: (lesson: string) => void;
  onDelete: () => void;
}

export function LessonRow({ lesson, onChange, onDelete }: LessonRowProps) {
  const { t } = useTranslation('debriefV2');
  const [draft, setDraft] = useState(lesson);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setDraft(lesson);
  }, [lesson]);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    textarea.style.height = `${textarea.scrollHeight}px`;
  }, [draft]);

  const commit = () => {
    if (draft === lesson) return;
    onChange(draft);
  };

  return (
    <div className="flex items-start gap-2.5 w-full bg-white border border-slate-200 shadow-sm rounded-lg p-3 focus-within:ring-2 focus-within:ring-indigo-500/20 focus-within:border-indigo-500 transition-all group">
      <div className="shrink-0 mt-0.5">
        <CheckCircle2
          className="h-[18px] w-[18px] text-emerald-500 fill-emerald-50"
          aria-hidden
        />
      </div>
      <textarea
        ref={textareaRef}
        rows={2}
        value={draft}
        placeholder={t('lessons.lessonPlaceholder')}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        className="flex-1 text-sm text-slate-900 placeholder:text-slate-400 bg-transparent outline-none resize-none overflow-hidden"
      />
      <button
        type="button"
        onMouseDown={(event) => {
          event.preventDefault();
          onDelete();
        }}
        className="shrink-0 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity p-0.5"
        aria-label={t('lessons.deleteLessonAria')}
      >
        <X className="h-4 w-4" aria-hidden />
      </button>
    </div>
  );
}
